import React, { useState } from "react";
import {
  Tooltip,
  Typography,
  Grid,
  Paper,
  Table,
  TableHead,
  TableBody,
  Card,
} from "@mui/material";
import {
  StyledButton,
  StyledTextField,
  StyledTableCellHistory,
  StyledTableRowHistory,
  OutlinedIconFab,
  StyledTableContainer,
} from "../StyledComponents";
import Icon from "../Utils/Icons";

const AdultVaccinationHistorySimplified = ({
  adultVaccinationHistoryData,
  setAdultVaccinationHistoryData,
  isAiEnabled,
}) => {
  const [vaccineName, setVaccineName] = useState("");
  const [dose, setDose] = useState("");
  const [dateOfAdministration, setDateOfAdministration] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState("");

  const handleAdd = () => {
    if (!vaccineName.trim()) {
      setError("Vaccine Name is required.");
      return;
    }
    const newRow = {
      vaccine_name: vaccineName.trim(),
      dose: dose.trim(),
      date_of_administration: dateOfAdministration || null,
    };
    const updatedRows = [...(adultVaccinationHistoryData?.new ?? []), newRow];
    setAdultVaccinationHistoryData({
      ...(adultVaccinationHistoryData ?? { prev: [], new: [] }),
      new: updatedRows,
    });
    resetForm();
  };

  const handleUpdate = () => {
    if (!vaccineName.trim()) {
      setError("Vaccine Name is required.");
      return;
    }
    const updatedRow = {
      vaccine_name: vaccineName.trim(),
      dose: dose.trim(),
      date_of_administration: dateOfAdministration || null,
    };
    const updated = (adultVaccinationHistoryData?.new ?? []).map((row, i) =>
      i === editingIndex ? updatedRow : row
    );
    setAdultVaccinationHistoryData({
      ...(adultVaccinationHistoryData ?? { prev: [], new: [] }),
      new: updated,
    });
    resetForm();
  };

  const handleEdit = (row, index) => {
    setVaccineName(row.vaccine_name || "");
    setDose(row.dose || "");
    setDateOfAdministration(row.date_of_administration || "");
    setEditingIndex(index);
    setError("");
  };

  const handleDelete = (index) => {
    const updated = (adultVaccinationHistoryData?.new ?? []).filter(
      (_, i) => i !== index
    );
    setAdultVaccinationHistoryData({
      ...(adultVaccinationHistoryData ?? { prev: [], new: [] }),
      new: updated,
    });
  };

  const resetForm = () => {
    setVaccineName("");
    setDose("");
    setDateOfAdministration("");
    setEditingIndex(null);
    setError("");
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        boxShadow: "0 4px 4px 0 #C7C7CC inset",
        backgroundColor: "#FFF",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{ margin: "5px 0px", display: "flex", alignItems: "center" }}
      >
        <Grid item xs={11} sm={11} md={11}>
          <StyledTextField
            label="Vaccine Name"
            variant="outlined"
            value={vaccineName}
            onChange={(e) => { setVaccineName(e.target.value); setError(""); }}
            fullWidth
            InputProps={{
              endAdornment: (
                <Tooltip title="Clear">
                  <OutlinedIconFab size="small" onClick={() => setVaccineName("")}>
                    <Icon name="clearTextField" size={32} />
                  </OutlinedIconFab>
                </Tooltip>
              ),
            }}
          />
        </Grid>

        <Grid item xs={11} sm={6} md={5}>
          <StyledTextField
            label="Dose"
            variant="outlined"
            value={dose}
            onChange={(e) => setDose(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={11} sm={6} md={6}>
          <StyledTextField
            label="Date of Administration"
            type="date"
            variant="outlined"
            value={dateOfAdministration}
            onChange={(e) => setDateOfAdministration(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={11} md={2} sx={{ display: "flex", justifyContent: { xs: "stretch", md: "flex-end" } }}>
          <StyledButton
            bgColor="#0E3F66"
            onClick={editingIndex !== null ? handleUpdate : handleAdd}
            variant="contained"
            sx={{ width: { xs: "100%" }, marginLeft: { md: "auto" } }}
            color="defaultColor"
          >
            {editingIndex !== null ? "Update" : "Add"}
          </StyledButton>
        </Grid>

        {error && (
          <Grid item xs={11}>
            <Typography style={{ color: "red" }}>{error}</Typography>
          </Grid>
        )}

        {(adultVaccinationHistoryData?.prev ?? []).length > 0 && (
          <Grid item xs={11}>
            <StyledTableContainer component={Paper}>
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>Vaccine Name</StyledTableCellHistory>
                    <StyledTableCellHistory>Dose</StyledTableCellHistory>
                    <StyledTableCellHistory>Date</StyledTableCellHistory>
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {(adultVaccinationHistoryData?.prev ?? []).map((row, i) => (
                    <StyledTableRowHistory key={i}>
                      <StyledTableCellHistory>{row.vaccine_name}</StyledTableCellHistory>
                      <StyledTableCellHistory>{row.dose || "-"}</StyledTableCellHistory>
                      <StyledTableCellHistory>{row.date_of_administration || "-"}</StyledTableCellHistory>
                      <StyledTableCellHistory>-</StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Grid>
        )}

        {(adultVaccinationHistoryData?.new ?? []).length > 0 && (
          <Grid item xs={11}>
            <StyledTableContainer component={Paper}>
              <Table>
                <TableHead>
                  <StyledTableRowHistory>
                    <StyledTableCellHistory>Vaccine Name</StyledTableCellHistory>
                    <StyledTableCellHistory>Dose</StyledTableCellHistory>
                    <StyledTableCellHistory>Date</StyledTableCellHistory>
                    <StyledTableCellHistory>Action</StyledTableCellHistory>
                  </StyledTableRowHistory>
                </TableHead>
                <TableBody>
                  {(adultVaccinationHistoryData?.new ?? []).map((row, i) => (
                    <StyledTableRowHistory key={i}>
                      <StyledTableCellHistory>{row.vaccine_name}</StyledTableCellHistory>
                      <StyledTableCellHistory>{row.dose || "-"}</StyledTableCellHistory>
                      <StyledTableCellHistory>{row.date_of_administration || "-"}</StyledTableCellHistory>
                      <StyledTableCellHistory>
                        <div style={{ display: "flex", flexDirection: "row" }}>
                          <Tooltip title="Edit">
                            <OutlinedIconFab onClick={() => handleEdit(row, i)} sx={{ marginRight: 1 }}>
                              <Icon name="editInTable" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <OutlinedIconFab onClick={() => handleDelete(i)}>
                              <Icon name="deleteInTable" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                        </div>
                      </StyledTableCellHistory>
                    </StyledTableRowHistory>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default React.memo(AdultVaccinationHistorySimplified);
